import { arrayOf, company, empty, isString, json, MEMBERS_TIMEOUT_MS, record, send, type Outcome } from './members'

/** The expiries a link can be created with, in days. */
export const SHARE_DAYS = [1, 7, 30] as const
export type ShareDays = (typeof SHARE_DAYS)[number]

/** The longest label the backend stores, in characters. */
export const LABEL_MAX = 80

/** One live row of `GET /api/share-links`. */
export interface ShareLink {
  id: string
  label: string | null
  createdBy: string
  createdAt: string
  expiresAt: string
  lastOpenedAt: string | null
}

/** The company a guest grant is for, as `open` and the guest session name it. */
export interface GuestGrant {
  companyId: string
  companyName: string
  expiresAt: string
}

function linkOf(value: unknown): ShareLink | null {
  const v = record(value)
  if (v === null) return null
  const { id, label, createdBy, createdAt, expiresAt, lastOpenedAt } = v
  if (!isString(id) || !isString(createdBy) || !isString(createdAt) || !isString(expiresAt)) return null
  if (label !== null && !isString(label)) return null
  if (lastOpenedAt !== null && !isString(lastOpenedAt)) return null
  return { id, label, createdBy, createdAt, expiresAt, lastOpenedAt }
}

export function grantOf(value: unknown): GuestGrant | null {
  const v = record(value)
  if (v === null) return null
  const { companyId, companyName, expiresAt } = v
  if (!isString(companyId) || companyId === '' || !isString(companyName) || !isString(expiresAt)) return null
  return { companyId, companyName, expiresAt }
}

export function listShareLinks(companyId: string): Promise<Outcome<ShareLink[]>> {
  return send(`/api/share-links?${company(companyId)}`, {}, (body) => arrayOf(record(body)?.links, linkOf))
}

/** The answer is the one place the link's token appears. */
export function createShareLink(
  companyId: string,
  days: ShareDays,
  label: string | null,
): Promise<Outcome<{ link: ShareLink; token: string }>> {
  return send(`/api/share-links?${company(companyId)}`, json('POST', { days, label }), (body) => {
    const v = record(body)
    const link = linkOf(v?.link)
    return link !== null && isString(v?.token) && v.token !== '' ? { link, token: v.token } : null
  })
}

export function revokeShareLink(companyId: string, id: string): Promise<Outcome<object>> {
  return send(`/api/share-links/${encodeURIComponent(id)}?${company(companyId)}`, { method: 'DELETE' }, empty)
}

/** The address a link is shared as: the token rides in the fragment, never in a request line. */
export function shareUrl(token: string, origin: string = window.location.origin): string {
  return `${origin}/share#${token}`
}

/**
 * `ended`: the link does not work (`404 link_unavailable`). `unavailable`: a `429`, a
 * `503`, a `403` for the origin, a network failure or a malformed answer; the token is
 * worth posting again.
 */
export type OpenResult =
  | { status: 'ok'; guest: GuestGrant }
  | { status: 'ended' }
  | { status: 'unavailable' }

/** `POST /api/share-links/open`. Needs no session, so it carries no generation check. */
export async function openShareLink(token: string): Promise<OpenResult> {
  let response: Response
  try {
    response = await fetch('/api/share-links/open', {
      credentials: 'same-origin',
      signal: AbortSignal.timeout(MEMBERS_TIMEOUT_MS),
      ...json('POST', { token }),
    })
  } catch {
    return { status: 'unavailable' }
  }
  if (response.status === 404) return { status: 'ended' }
  if (!response.ok) return { status: 'unavailable' }
  try {
    const guest = grantOf(record(await response.json())?.guest)
    return guest === null ? { status: 'unavailable' } : { status: 'ok', guest }
  } catch {
    return { status: 'unavailable' }
  }
}

/**
 * `POST /api/share-links/leave`: clears this browser's guest cookie. `true` on `204`, and
 * on `401`, which the guard answers for a link that already ended, clearing the cookie
 * itself. Anything else leaves the cookie in place, so the caller keeps the view.
 */
export async function leaveGuestView(): Promise<boolean> {
  try {
    const response = await fetch('/api/share-links/leave', {
      method: 'POST',
      credentials: 'same-origin',
      signal: AbortSignal.timeout(MEMBERS_TIMEOUT_MS),
    })
    return response.status === 204 || response.status === 401
  } catch {
    return false
  }
}
